import React, { useCallback, useEffect, useMemo, useRef } from "react";

import {
  faFileImport,
  faLock,
  faMobile,
  faPlus,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import {
  invalidateTableView,
  requestPropertyERPCheck,
  setSetting,
} from "actions";

import AgGridTable from "components/AgGrid/AgGridContainer";
import WaitForSync from "components/LoadingSpinner/WaitForSync";

import { AgGridTables } from "constants/aggrid";
import { Column } from "constants/columns";
import { ApiModel } from "constants/loading";
import {
  ConsignmentModalSection,
  ModalTypes,
  SaleLotModalSection,
} from "constants/navigation";
import {
  ConsignmentPermissions,
  DeploymentPermissions,
  SaleyardPermissions,
} from "constants/permissions";
import { SaleType } from "constants/sale";
import { Settings } from "constants/settings";
import { userTypes } from "constants/users";

import {
  getLivestockSaleId,
  getSaleRoute,
  getSaleyardName,
  openCsvSaleImport,
  openEditConsignmentModal,
  openEditSaleLotModal,
  openImportXml,
  openModalLink,
} from "lib/navigation";
import { hasPermission } from "lib/permissions";
import { disableReadOnlyActions } from "lib/readOnly";

import {
  currentSaleSelector,
  getCurrentAgenciesOptions,
  getCurrentSale,
  getCurrentSaleyard,
  getHasWriteAccessInCurrentSale,
  getIsConsigningAgent,
  getIsConsigningAgentAndSaleUnlocked,
  getIsSaleyardAdmin,
  getSetting,
  selectCurrentDeployments,
  selectGeneralConsignmentsAggridData,
  selectIsUserOfType,
} from "selectors";

import {
  useHasManualChargesPermission,
  useSetScreenSize,
  useHasAddConsignmentPermission,
  useHasDeploymentPermission,
  useHasPermission,
  useNvdUpload,
  useSomeHasPermission,
  useHasExternalIntegrationsPermission,
  useViewAttachmentsAction,
} from "hooks";

import { defaultConsignmentColumns } from "./columnDefs";

const getRowId = params => params.data.id;

const rowClassRules = {
  "row-warning": params => {
    const { api, node } = params;
    const rowWarning = api.getCellValue({
      colKey: Column.Consignment.WARNING,
      rowNode: node,
    });
    return !!rowWarning;
  },
};

const ConsignmentsTable = () => {
  const dispatch = useDispatch();
  const rowData = useSelector(selectGeneralConsignmentsAggridData);
  const isHooksSale =
    useSelector(getCurrentSale)?.sale_type === SaleType.OVER_HOOKS;
  const { setMobile } = useSetScreenSize();
  const hasReceivalLotPermission = useHasPermission(
    getCurrentSaleyard,
    SaleyardPermissions.featureReceivalLots,
  );

  const tableName = AgGridTables.CONSIGNMENT;
  const showCurrentAgencyFilter = useSelector(
    selectIsUserOfType([userTypes.BUSINESS_USER, userTypes.SALEYARD_ADMIN]),
  );
  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);
  const sale = useSelector(currentSaleSelector);
  const agencyId = useSelector(getSetting(Settings.agencyId));
  const agencyIdRef = useRef(agencyId);

  useEffect(() => {
    if (agencyIdRef.current !== agencyId) {
      dispatch(invalidateTableView(AgGridTables.CONSIGNMENT));
      agencyIdRef.current = agencyId;
    }
  }, [agencyId, agencyIdRef, dispatch]);

  const doesExternalFilterPass = useCallback(
    ({ data }) => agencyId === null || data.livestockAgency?.id === agencyId,
    [agencyId],
  );

  const isExternalFilterPresent = useCallback(
    () => agencyId !== null,
    [agencyId],
  );

  const handleClickSaleLotComments = ({ id }) => {
    openEditConsignmentModal(id, ConsignmentModalSection.SALE_LOTS, true);
  };

  const { openUpload, uploadModal } = useNvdUpload();

  const hasWriteAccessInCurrentSale = useSelector(
    getHasWriteAccessInCurrentSale,
  );
  const isConsigningAgentAndSaleUnlocked = useSelector(
    getIsConsigningAgentAndSaleUnlocked,
  );
  const isConsigningAgent = useSelector(getIsConsigningAgent);

  const readOnly = !hasWriteAccessInCurrentSale || isConsigningAgent;

  const hasNominationsFeaturePermission = useSomeHasPermission(
    selectCurrentDeployments,
    DeploymentPermissions.featureNominations,
  );

  const hasExternalIntegrationPermission =
    useHasExternalIntegrationsPermission();

  const context = useMemo(
    () => ({
      showCurrentAgencyFilter,
      hasWriteAccess: true,
      handleClickSaleLotComments,
      openUploadModal: openUpload,
      readOnly,
    }),
    [openUpload, readOnly, showCurrentAgencyFilter],
  );

  const {
    date: saleDate,
    saleyard_name: saleyard,
    species_id: speciesId,
  } = sale;

  const agencyOptions = useSelector(getCurrentAgenciesOptions(false));

  const hasAddConsignmentPermission = useHasAddConsignmentPermission();
  const hasManualChargePermission = useHasManualChargesPermission();

  const hasCreateConsignmentPermission = useHasDeploymentPermission(
    DeploymentPermissions.canAddConsignment,
  );

  const viewAttachmentsAction = useViewAttachmentsAction({ default: true });

  const bulkUploadAction = useMemo(() => {
    if (!isSaleyardAdmin || agencyId) {
      return {
        title: "Bulk Upload Attachments",
        icon: hasWriteAccessInCurrentSale ? faUpload : faLock,
        onClick: () => {
          openUpload(null);
        },
        default: true,
        dataTour: "bulkUpload",
        isDisabled: !hasCreateConsignmentPermission,
      };
    }
    return {
      title: "Bulk Upload Attachments",
      icon: hasWriteAccessInCurrentSale ? faUpload : faLock,
      default: true,
      dataTour: "bulkUpload",
      isDisabled: !hasCreateConsignmentPermission,
      onClick:
        agencyOptions.length === 1
          ? () => {
              dispatch(setSetting(Settings.agencyId, agencyOptions[0].value));
              openUpload(null);
            }
          : null,
      subNavItems:
        agencyOptions.length > 1
          ? agencyOptions.map(agency => ({
              title: agency.label,
              dataTour: `bulkUpload:${agency.label}`,
              onClick: () => {
                dispatch(setSetting(Settings.agencyId, agency.value));
                openUpload(null);
              },
            }))
          : [],
    };
  }, [
    agencyId,
    agencyOptions,
    dispatch,
    hasWriteAccessInCurrentSale,
    isSaleyardAdmin,
    hasCreateConsignmentPermission,
    openUpload,
  ]);
  const history = useHistory();
  const additionalActions = useMemo(
    () =>
      [
        {
          title: "Mobile View",
          icon: faMobile,
          onClick: () => {
            setMobile();
            history.push(
              `${getSaleRoute(
                getSaleyardName(),
                getLivestockSaleId(),
              )}/consignment-cards`,
            );
          },
          dataTour: "mobileView",
        },
        {
          title: "Consignment",
          icon: faPlus,
          onClick: () => openEditConsignmentModal(),
          default: true,
          dataTour: "addConsignment",
          // if it is a consigning agent and the sale is unlocked
          // it should not be read only, otherwise use the permission check
          readOnly: isConsigningAgentAndSaleUnlocked
            ? false
            : !hasAddConsignmentPermission,
        },
        bulkUploadAction,
        viewAttachmentsAction,

        !isSaleyardAdmin && {
          title: "Import Sale XML",
          icon: faFileImport,
          onClick: () => openImportXml(sale),
          dataTour: "import",
          isDisabled: !hasCreateConsignmentPermission,
        },
        !isSaleyardAdmin && {
          title: "Import Sale CSV",
          icon: hasCreateConsignmentPermission ? faFileImport : faLock,
          onClick: () => openCsvSaleImport(),
          dataTour: "importCsv",
          isDisabled: !hasCreateConsignmentPermission,
        },
      ]
        .filter(Boolean)
        .map(disableReadOnlyActions),
    [
      isConsigningAgentAndSaleUnlocked,
      hasAddConsignmentPermission,
      bulkUploadAction,
      viewAttachmentsAction,
      isSaleyardAdmin,
      history,
      sale,
      setMobile,
      hasCreateConsignmentPermission,
    ],
  );

  const extraHeaderComponents = useMemo(
    () => [uploadModal].filter(Boolean),
    [uploadModal],
  );

  const getContextMenuItems = useCallback(
    params => {
      const contextMenu = Array.isArray(params.defaultItems)
        ? [...params.defaultItems]
        : [];
      // If they actually clicked a row, vs whitespace.
      if (params.node) {
        const rowData = params.node.data;
        const hasUpdateConsignmentPermission = hasPermission(
          rowData,
          ConsignmentPermissions.update,
        );

        if (params.column.colId === "nlisPrograms") {
          const propertyId = rowData.vendor_property_id;
          contextMenu.push({
            name: "Refresh NLIS Status",
            disabled: !propertyId,
            action: () => dispatch(requestPropertyERPCheck(propertyId)),
          });
        }

        if (hasManualChargePermission) {
          contextMenu.push({
            name: "Charge Vendor (Add Sundry)",
            action: () => {
              const modalProps = {
                id: null,
                fromBusinessId: null,
                toBusinessId: rowData.vendor_id,
                note: "",
              };
              openModalLink(ModalTypes.ManualAdjustmentForm, modalProps);
            },
          });
          contextMenu.push({
            name: "Credit Vendor (Add Sundry)",
            action: () => {
              const modalProps = {
                id: null,
                fromBusinessId: rowData.vendor_id,
                toBusinessId: null,
                note: "",
              };
              openModalLink(ModalTypes.ManualAdjustmentForm, modalProps);
            },
          });
        }

        const consignmentReadOnly = readOnly || !hasUpdateConsignmentPermission;
        const hasAddSaleLotPermission = hasPermission(
          rowData,
          ConsignmentPermissions.canAddSaleLot,
        );

        contextMenu.push({
          name: consignmentReadOnly ? "View Consignment" : "Edit Consignment",
          action: () =>
            openEditConsignmentModal(params.node.data.id, undefined, true),
        });

        if (!consignmentReadOnly && hasAddSaleLotPermission) {
          contextMenu.push({
            name: "Add Lot",
            action: () =>
              openEditSaleLotModal(
                null,
                SaleLotModalSection.GENERAL,
                "",
                rowData.id,
                null,
                window.location.hash,
              ),
          });
        }
      }

      return contextMenu;
    },
    [dispatch, hasManualChargePermission, readOnly],
  );

  const columnDefs = useMemo(() => {
    const defs = defaultConsignmentColumns(
      isHooksSale,
      hasReceivalLotPermission,
      speciesId,
    );
    if (!hasNominationsFeaturePermission) {
      const nominationChildren =
        defs.find(
          columnDef => columnDef.colId === Column.Consignment.NOMINATION,
        )?.children || [];
      nominationChildren.forEach(child => {
        child.suppressColumnsToolPanel = true;
        child.hide = true;
      });
    }
    if (!hasExternalIntegrationPermission) {
      const abnCheckColumn = defs.find(
        columnDef => columnDef.colId === Column.Consignment.ABN_CHECK,
      );
      if (abnCheckColumn) {
        abnCheckColumn.suppressColumnsToolPanel = true;
        abnCheckColumn.hide = true;
      }
    }
    return defs;
  }, [
    isHooksSale,
    hasReceivalLotPermission,
    speciesId,
    hasNominationsFeaturePermission,
    hasExternalIntegrationPermission,
  ]);
  return (
    <AgGridTable
      additionalActions={additionalActions}
      columnDefs={columnDefs}
      context={context}
      doesExternalFilterPass={doesExternalFilterPass}
      downloadFilename={`${saleDate}-${saleyard}-Consignments.csv`}
      extraActionComponents={extraHeaderComponents}
      getContextMenuItems={getContextMenuItems}
      getRowId={getRowId}
      isExternalFilterPresent={isExternalFilterPresent}
      rowData={rowData}
      showSimpleAgencyFilter
      tableName={tableName}
      rowClassRules={hasReceivalLotPermission ? rowClassRules : undefined}
    />
  );
};

const LoadingWrapper = props => (
  <WaitForSync
    requiredData={[
      ApiModel.SALE_LOTS,
      ApiModel.CONSIGNMENTS,
      ApiModel.DEPLOYMENTS,
      ApiModel.SALEYARDS,
      ApiModel.ROUNDS,
      ApiModel.SPECIES,
    ]}
    subjectName="consignments"
  >
    <ConsignmentsTable {...props} />
  </WaitForSync>
);

export default LoadingWrapper;
